/**
 * Where the doors and drawers divide, read off a photograph.
 *
 * A LiDAR scan gives the room and the runs to the millimetre and it cannot give this. A door
 * separation is a 3mm reveal on a flat front and an iPhone resolves at 10 to 20, so four
 * doors, a bank of drawers and a pantry come back from the scanner as ONE FLAT PLANE. The
 * divisions are in the camera image, not in the depth -- and the camera, for its part, cannot
 * give a millimetre.
 *
 * So each is used for what it is good at. The scan supplies the run's length and every
 * appliance's measured position; the photograph supplies the SEQUENCE of fronts and their
 * PROPORTIONS, as shares of the run that sum to one. Shares rather than millimetres because
 * that is the honest limit of what a photograph knows: a picture can say "the sink cupboard
 * is about a quarter of that wall" and cannot say 906. It is also why one photo describes
 * both the base run and the overhead run above it -- proportions are the same picture read
 * twice.
 *
 * This file snaps the second onto the first. Fixed points keep the positions the scan
 * measured, the reader's plain fronts divide what is between them, every width comes from
 * the maker's own range or from a stretch they have said they will cut, and the run comes
 * out EXACTLY full -- which is the promise every other part of the app is built on and the
 * reason a homeowner cannot send their maker something unbuildable.
 *
 * Pure. No DOM, no Vue, no network, no three.js. It is the part worth testing.
 */

import { sum, fill, offsets } from './layout.js'

/** Millimetres of slop we treat as zero, the same figure layout.js uses. */
const EPS = 0.5
/**
 * And the most a width may sit outside what the maker cuts, to close the arithmetic.
 *
 * A twentieth of a millimetre. Deliberately far under `EPS`, because `faults` judges a
 * cabinet against its stretch range at half a millimetre and a run settled to exactly that
 * would be a run that passes or fails on a rounding mood.
 */
const SLIVER = 0.05
/** And the figure the arithmetic itself is driven to, so slop cannot accumulate over gaps. */
const TINY = 1e-6

/**
 * The roles the SCAN measured, and the photograph may not overrule.
 *
 * A tape measure beats a camera on every one of these. A sink is plumbed where it is
 * plumbed; a fridge space was measured against the fridge that stands in it. The photo is
 * allowed to say what is BESIDE them and never where they are.
 */
export const FIXED_ROLES = ['sink', 'cooktop', 'oven', 'dishwasher', 'fridge', 'rangehood']

/**
 * Below this the reader is guessing, and a guess is not worth a redesign.
 *
 * The reader lowers its own confidence when the ends of the run are out of frame, which is
 * the common failure: a photo taken from the doorway of a long wall. Rather than divide a
 * run from half a picture we leave the scan's own answer alone and say why.
 */
export const MIN_CONFIDENCE = 0.35

const FRONT_KINDS = ['doors', 'drawers', 'appliance', 'open', 'blank']

const clamp = (n, lo, hi) => Math.min(Math.max(n, lo), hi)
const capOf = (spec, lim) => Math.min(spec.stretch?.[1] ?? spec.w, lim.maxCabinetWidth ?? Infinity)
const floorOf = (spec) => spec.stretch?.[0] ?? spec.w

/** One front as the reader could have meant it. Anything unreadable becomes a plain door. */
function tidy(f) {
  if (!f || typeof f !== 'object') return null
  return {
    kind: FRONT_KINDS.includes(f.kind) ? f.kind : 'doors',
    doors: Number.isFinite(f.doors) ? Math.max(0, Math.round(f.doors)) : 0,
    drawers: Number.isFinite(f.drawers) ? Math.max(0, Math.round(f.drawers)) : 0,
    role: typeof f.role === 'string' ? f.role : 'none',
    share: Number.isFinite(f.share) ? clamp(f.share, 0, 1) : 0,
  }
}

/**
 * An appliance the photograph saw and the scan did not, demoted to an ordinary front.
 *
 * The alternative -- inventing a fridge space where nothing was measured -- would put a
 * 900mm hole in a run on the strength of a camera, which is exactly the thing this module
 * exists not to do. A cupboard is the safe reading of an unexplained front.
 */
const asPlain = (f) => ({
  ...f,
  kind: f.drawers > 0 ? 'drawers' : 'doors',
  doors: f.doors > 0 ? f.doors : f.drawers > 0 ? 0 : 1,
})

/**
 * The cabinets in this maker's range that are the SHAPE the photograph reported.
 *
 * Shape first, width second. A photo that says "three drawers" is reporting the one thing a
 * camera is actually good at, so a 450 drawer bank is a better answer than a 600 cupboard
 * even though the 600 is nearer the width -- and only when the maker has nothing of that
 * shape narrow enough does the search widen to the whole range.
 */
function shapeSet(front, pool) {
  const roleless = pool.filter((c) => !c.role)
  if (front.kind === 'open') {
    const o = pool.filter((c) => c.role === 'open')
    if (o.length) return o
  }
  if (front.kind === 'drawers') {
    const d = roleless.filter((c) => (c.drawers ?? 0) === Math.max(1, front.drawers))
    if (d.length) return d
  }
  if (front.kind === 'doors') {
    const d = roleless.filter((c) => (c.doors ?? 0) === Math.max(1, front.doors) && !((c.drawers ?? 0) > 0))
    if (d.length) return d
  }
  return roleless.length ? roleless : pool
}

/**
 * The nearest cabinet in a set to a wanted width.
 *
 * Distance from the catalogue width, plus twice however far the want falls OUTSIDE what the
 * maker will cut that cabinet to -- which is what makes a 400 (350-450) beat a 300 (280-330)
 * for a 350mm front. A cabinet that has to be stretched to its limit to be there is a worse
 * answer than one that sits in the middle of its range, and it is the second that a factory
 * cuts without a phone call.
 */
function nearest(front, want, set, lim) {
  const score = (c) => {
    const lo = floorOf(c)
    const hi = capOf(c, lim)
    let s = Math.abs(c.w - want) + 2 * Math.max(0, lo - want, want - hi)
    if (front.kind === 'drawers') {
      s += Math.abs((c.drawers ?? 0) - Math.max(1, front.drawers)) * 500
      if (!(c.drawers > 0)) s += 500
    } else if (front.kind === 'doors') {
      s += Math.abs((c.doors ?? 0) - Math.max(1, front.doors)) * 500
      if ((c.drawers ?? 0) > 0) s += 500
    }
    return s
  }
  return set.slice().sort((a, b) => score(a) - score(b) || a.w - b.w)[0] ?? null
}

/**
 * One front, as the cabinets that will stand in it.
 *
 * Usually one. A photograph of a four-metre wall may report an unbroken row of doors as ONE
 * front, and nobody builds a four-metre cabinet -- so a front wider than the widest thing of
 * that shape the maker cuts becomes as many EQUAL cabinets as it takes. The alternative is
 * one cabinet at its limit with a metre of filler beside it, which is neither what is in the
 * picture nor what anyone would build.
 */
function build(front, want, pool, lim) {
  const asFiller = () => [{ specId: 'filler', filler: true,
    w: Math.max(want, lim.minFiller ?? 30), lo: lim.minFiller ?? 30, hi: Infinity }]
  if (front.kind === 'blank') return asFiller()

  let set = shapeSet(front, pool)
  if (!set.length) return asFiller()
  const split = (s) => {
    const widest = Math.max(...s.map((c) => capOf(c, lim)))
    const n = Math.max(1, Math.ceil(want / widest - TINY))
    return { n, each: want / n }
  }
  let { n, each } = split(set)
  // Nothing of that shape is narrow enough to stand in the space the photo gave it: take the
  // width over the shape rather than overfill the run, and let the whole range answer.
  if (Math.min(...set.map(floorOf)) > each + TINY) {
    const all = pool.filter((c) => !c.role)
    if (all.length) { set = all; ({ n, each } = split(set)) }
  }
  const spec = nearest(front, each, set, lim)
  if (!spec) return asFiller()
  const lo = floorOf(spec)
  const hi = capOf(spec, lim)
  return Array.from({ length: n }, () => ({ specId: spec.id, spec, w: clamp(each, lo, hi), lo, hi }))
}

/**
 * Make a chosen row add up to exactly the gap it is standing in, or say it cannot.
 *
 * Short: widen, widest first, no further than the maker cuts, and a filler for what is
 * left. Over: shrink by the same rule. Null when neither works -- and null is not a failure,
 * it is the signal to the caller to believe one front fewer and try again, which is what a
 * person does when a photo shows five doors on a wall that holds four.
 */
function settleGap(chosen, len, lim) {
  const out = chosen.map((c) => ({ ...c }))
  if (!out.length) return null
  const minFiller = lim.minFiller ?? 30
  let diff = len - sum(out)

  if (diff > TINY) {
    for (const c of out.slice().sort((a, b) => b.w - a.w)) {
      const room = c.hi - c.w
      if (room <= TINY) continue
      const add = Math.min(room, diff)
      c.w += add
      diff -= add
      if (diff <= TINY) return out
    }
    // A sliver too small to see goes on the end of the last cabinet -- twenty microns past
    // a stretch limit is not a cabinet the factory will query. Anything a maker WOULD make a
    // filler out of becomes one; anything between is a division that has not worked.
    if (diff <= SLIVER) { out[out.length - 1].w += diff; return out }
    if (diff >= minFiller) { out.push({ specId: 'filler', filler: true, w: diff, lo: minFiller, hi: Infinity }); return out }
    return null
  }

  let over = -diff
  if (over <= TINY) return out
  for (const c of out.slice().sort((a, b) => (b.w - b.lo) - (a.w - a.lo))) {
    const take = Math.min(Math.max(0, c.w - c.lo), over)
    if (take <= TINY) continue
    c.w -= take
    over -= take
    if (over <= TINY) return out
  }
  if (over <= SLIVER) { out[out.length - 1].w -= over; return out }
  return null
}

/** A gap the photograph said nothing about: filled the ordinary way, largest first. */
function plainFill(len, pool, lim) {
  const r = fill(len, pool.filter((c) => !c.role), lim)
  const out = r.items.map((it) => ({ ...it }))
  const left = len - sum(out)
  if (left > EPS) out.push({ specId: 'filler', filler: true, w: left })
  else if (Math.abs(left) > TINY && out.length) out[out.length - 1].w += left
  return out
}

/**
 * The stretch of run between two fixed points, divided by what the photograph saw in it.
 *
 * The shares are shares of the whole run, so within a gap they are normalised against each
 * other -- which is the same statement said locally, and it is what keeps the arithmetic
 * exact when the reader's shares do not quite sum to one.
 */
function fillGap(len, fronts, pool, lim, notes) {
  if (len <= EPS) return []
  let list = fronts.slice()
  while (list.length) {
    const total = list.reduce((n, f) => n + f.share, 0)
    const chosen = list.flatMap((f) => build(
      f, total > TINY ? len * f.share / total : len / list.length, pool, lim,
    ))
    const settled = settleGap(chosen, len, lim)
    if (settled) {
      if (list.length < fronts.length) {
        const n = fronts.length - list.length
        notes.push(n === 1
          ? 'One front in the photo was too narrow to build at that width, so it was left out.'
          : `${n} fronts in the photo were too narrow to build at that width, so they were left out.`)
      }
      return settled
    }
    // The narrowest is the one a photograph is least sure of, so it is the one to give up.
    let k = 0
    for (let i = 1; i < list.length; i++) if (list[i].share < list[k].share) k = i
    list = list.filter((_, i) => i !== k)
  }
  notes.push('One stretch of that wall could not be divided the way the photo showed it, '
    + 'so it was filled from the range.')
  return plainFill(len, pool, lim)
}

/**
 * A run, re-divided by what a photograph of that wall showed.
 *
 * `run` is the plan's run, `fronts` the reader's sequence for it, `maker` the range, and
 * `{ from, to }` the run's span along the wall as `room.extents` gives it. Returns a full
 * replacement for `run.items`, exactly `to - from` long, and the notes worth saying out loud.
 *
 * Nothing is applied here and nothing is mutated: the caller decides, and one Undo takes it
 * back.
 */
export function divideRun(run, fronts, maker, { from = 0, to = 0, confidence = 1 } = {}) {
  const notes = []
  const lim = maker.limits
  const length = to - from
  const list = (Array.isArray(fronts) ? fronts : []).map(tidy).filter(Boolean)

  if (!list.length) {
    return { items: run.items, notes: ['The photo did not show any fronts on that wall, so the cabinets are as the scan left them.'] }
  }
  if (!(confidence >= MIN_CONFIDENCE)) {
    return { items: run.items, notes: [`The reader was only ${Math.round(confidence * 100)}% sure of that photo, so the cabinets are as the scan left them.`] }
  }
  if (!(length > EPS)) {
    return { items: run.items, notes: ['That run has no length to divide.'] }
  }

  const pool = maker.cabinets.filter((c) => c.kind === run.kind)
  if (!pool.length) {
    return { items: run.items, notes: ['This maker has no cabinets of that kind, so the run is unchanged.'] }
  }

  /*
   * FIXED POINTS FIRST, at the positions the scan measured.
   *
   * A run's positions are implied by the order and the widths of its items, so a measured
   * position is read back out with `offsets` before anything is replaced, and the gaps
   * between them are what the photograph is then allowed to divide.
   */
  const items = run.items ?? []
  const offs = offsets(items)
  const specOf = (id) => maker.cabinets.find((c) => c.id === id) ?? null
  const anchors = []
  items.forEach((it, i) => {
    const spec = specOf(it.specId)
    if (spec && FIXED_ROLES.includes(spec.role)) {
      anchors.push({ item: it, spec, w: it.w, pos: from + offs[i] })
    }
  })
  anchors.sort((a, b) => a.pos - b.pos)
  let cursor = from
  for (const a of anchors) {
    a.pos = Math.max(cursor, Math.min(a.pos, to - a.w))
    cursor = a.pos + a.w
  }
  if (cursor > to + EPS) {
    return { items: run.items, notes: ['The appliances the scan measured already fill that run, so there was nothing to divide.'] }
  }

  /*
   * The reader's fronts, sorted into the gaps between those fixed points.
   *
   * The reader was told where the fixed points are, so its appliance fronts are the anchors
   * of the sequence: "the sink pair is the third front" is exactly the information a
   * photograph can give and a scan cannot use on its own. Matching is by role and in order,
   * skipping forward over an appliance the photo did not mention -- and an appliance the
   * photo saw that the scan did not measure becomes an ordinary front rather than a new
   * hole in the run.
   */
  const buckets = Array.from({ length: anchors.length + 1 }, () => [])
  let g = 0
  let invented = 0
  for (const f of list) {
    if (f.kind === 'appliance') {
      let k = -1
      for (let i = g; i < anchors.length; i++) if (anchors[i].spec.role === f.role) { k = i; break }
      if (k >= 0) { g = k + 1; continue }
      invented += 1
      buckets[g].push(asPlain(f))
      continue
    }
    buckets[g].push(f)
  }
  if (invented) {
    notes.push(`The photo shows ${invented} appliance${invented > 1 ? 's' : ''} the scan did not measure; `
      + 'they were built as cupboards rather than guessed at.')
  }

  const out = []
  let stamp = 0
  // Widths are NOT rounded here. A tenth off each of a dozen cabinets is a millimetre off
  // the run, and "exactly full" is the one promise this file exists to keep; the schedule
  // and the drawings round for display, where rounding is free.
  const clean = (c) => {
    const it = { id: `${run.id}-p${stamp++}`, specId: c.specId, w: c.w }
    if (c.filler) { it.filler = true; return it }
    // `stretched` is read off the range rather than carried through the arithmetic, so a
    // cabinet that was widened and then narrowed back does not claim to have been cut.
    const spec = c.spec ?? specOf(c.specId)
    if (spec && Math.abs(c.w - spec.w) > EPS) it.stretched = true
    return it
  }
  for (let i = 0; i <= anchors.length; i++) {
    const lo = i === 0 ? from : anchors[i - 1].pos + anchors[i - 1].w
    const hi = i === anchors.length ? to : anchors[i].pos
    for (const c of fillGap(hi - lo, buckets[i], pool, lim, notes)) out.push(clean(c))
    if (i < anchors.length) out.push({ ...anchors[i].item, w: anchors[i].w })
  }

  /*
   * And the promise, checked rather than trusted.
   *
   * Every gap above settles to its own length, so this should never fire -- which is exactly
   * why it is here. A run that is short by a millimetre is a kitchen with a millimetre
   * missing, and the honest repair is a filler, never a cabinet pushed outside what the
   * maker cuts.
   */
  let off = length - sum(out)
  if (off > EPS) {
    out.push({ id: `${run.id}-p${stamp++}`, specId: 'filler', filler: true, w: off })
    notes.push('A strip of that wall was left over and became a filler.')
  } else if (off < -EPS) {
    for (const it of out.slice().reverse()) {
      if (!it.filler || off >= -TINY) continue
      const take = Math.min(it.w, -off)
      it.w -= take
      off += take
    }
    if (off < -EPS) notes.push(`That wall holds ${Math.round(-off)}mm more cabinet than the scan measured.`)
  }

  return { items: out, notes }
}
